import "./CertificadosEditor.css";

import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
    FaArrowLeft,
    FaDatabase,
    FaEye,
    FaFileAlt,
    FaFilePdf,
    FaFileCode,
    FaLayerGroup,
    FaMagnet,
    FaRedo,
    FaSearch,
    FaSave,
    FaEdit,
    FaFont,
    FaTh,
    FaTimes,
    FaUndo
} from "react-icons/fa";
import EditorCanvas from "../EditorCanvas/EditorCanvas";
import EditorPropiedades from "../EditorPropiedades/EditorPropiedades";
import EditorPageProperties from "../EditorPropiedades/EditorPageProperties";
import EditorSidebar from "../EditorSidebar/EditorSidebar";
import MobileCertificateEditor from "./MobileCertificateEditor";
import CertificateSampleDataPanel from "./CertificateSampleDataPanel";
import CanvasZoomControls from "../CanvasZoomControls";
import { buildCertificatePreviewHtml, downloadCertificateJson, downloadCertificatePdf } from "../certificateHtml";
import { getCertificateDesignStorageKey, getCertificateSampleDataStorageKey } from "../certificateStorage";
import { xsdTemplateApi } from "../../xsd/api/xsdTemplate.api";
import { xsdApi } from "../../xsd/api/xsd.api";
import useMobileEditorViewport from "../useMobileEditorViewport";

const HISTORY_LIMIT = 100;
const createEmptyDocument = () => ({
    elements: [],
    pageStyle: { backgroundColor: "#FFFFFF", texture: "plain" }
});

const getXsdFieldPaths = catalog => {
    const fields = new Set();
    const visit = node => {
        if (node.path) fields.add(node.path);
        (node.attributes || []).forEach(attribute => {
            if (attribute.fullPath) fields.add(attribute.fullPath);
        });
        (node.children || []).forEach(visit);
    };
    catalog.forEach(visit);
    return Array.from(fields);
};

const getXsdSampleValues = (xsd, xsdVersion) => {
    const versionSchema = (Array.isArray(xsd?.versions) ? xsd.versions : [])
        .find(version => String(version.version) === String(xsdVersion));
    if (!versionSchema) {
        throw new Error(`No se encontró la versión ${xsdVersion} en la respuesta del XSD.`);
    }

    const structure = versionSchema.structure || versionSchema.estructura || versionSchema.nodos;
    if (!Array.isArray(structure)) {
        throw new Error(`La estructura de la versión ${xsdVersion} tiene un formato inválido.`);
    }

    const sampleValues = {};
    const visitNode = (node, parentPath = "") => {
        const nodeName = node.name || node.nodeName;
        const currentPath = parentPath ? `${parentPath}.${nodeName}` : nodeName;
        const items = node.items || node.campos || [];

        items.forEach(item => {
            const itemName = item.itemName || item.nombre || item.name;
            const path = `${currentPath}.${itemName}`;
            const fixedValue = item.fixedValue;
            const value = fixedValue !== null && fixedValue !== undefined && fixedValue !== ""
                ? item.fixedValue
                : item.defaultValue;
            if (itemName && value !== null && value !== undefined && value !== "") {
                sampleValues[path] = String(value);
            }
        });

        const children = node.childrens || node.children || node.nodos || [];
        children.forEach(child => visitNode(child, currentPath));
    };
    structure.forEach(node => visitNode(node));
    return sampleValues;
};

const loadSavedDocument = storageKey => {
    try {
        const savedDocument = window.localStorage.getItem(storageKey);
        if (!savedDocument) return createEmptyDocument();

        const document = JSON.parse(savedDocument);
        if (!Array.isArray(document.elements) || !document.pageStyle) {
            return createEmptyDocument();
        }
        return document;
    } catch {
        return createEmptyDocument();
    }
};

const loadSavedSampleData = storageKey => {
    try {
        const savedSampleData = window.localStorage.getItem(storageKey);
        if (!savedSampleData) return {};

        const parsed = JSON.parse(savedSampleData);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        return Object.fromEntries(
            Object.entries(parsed).filter(([, value]) => typeof value === "string")
        );
    } catch {
        return {};
    }
};

function CertificadosEditor({ onBack, xsdId = null, xsdName = "", xsdVersion = null }) {
    const hasXsdId = xsdId !== null && xsdId !== undefined && xsdId !== "";
    const isVersioned = hasXsdId && Boolean(xsdVersion);
    const requiresVersion = hasXsdId && !xsdVersion;
    const documentStorageKey = getCertificateDesignStorageKey(xsdId, xsdVersion);
    const sampleDataStorageKey = getCertificateSampleDataStorageKey(xsdId, xsdVersion);
    const isMobileEditor = useMobileEditorViewport();
    const [history, setHistory] = useState(() => ({
        past: [],
        present: isVersioned || !hasXsdId ? loadSavedDocument(documentStorageKey) : createEmptyDocument(),
        future: []
    }));
    const [loadState, setLoadState] = useState(isVersioned ? "loading" : "ready");
    const [loadError, setLoadError] = useState("");
    const [dynamicFields, setDynamicFields] = useState(isVersioned ? [] : null);
    const [sampleData, setSampleData] = useState(() => loadSavedSampleData(sampleDataStorageKey));
    const [sampleDataOpen, setSampleDataOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedElement, setSelectedElement] = useState(null);
    const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);
    const [activePanel, setActivePanel] = useState(null);
    const [gridVisible, setGridVisible] = useState(false);
    const [snapToGrid, setSnapToGrid] = useState(false);
    const [canvasZoom, setCanvasZoom] = useState(1);
    const [previewHtml, setPreviewHtml] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [pdfError, setPdfError] = useState("");
    const { elements, pageStyle } = history.present;

    useEffect(() => {
        if (!isVersioned) return undefined;

        const numericXsdId = Number(xsdId);
        if (!Number.isSafeInteger(numericXsdId) || numericXsdId <= 0) {
            setLoadError("El ID del XSD no es válido.");
            setLoadState("error");
            return undefined;
        }

        let cancelled = false;
        setLoadState("loading");
        setLoadError("");

        Promise.all([
            xsdTemplateApi.getElements(numericXsdId, xsdVersion),
            xsdApi.getById(numericXsdId)
        ])
            .then(([catalog, xsd]) => {
                if (!Array.isArray(catalog)) {
                    throw new Error("El catálogo de campos del XSD tiene un formato inválido.");
                }
                const schemaDefaults = getXsdSampleValues(xsd, xsdVersion);
                const savedSamples = loadSavedSampleData(sampleDataStorageKey);
                if (cancelled) return;
                setDynamicFields(getXsdFieldPaths(catalog));
                setSampleData({ ...schemaDefaults, ...savedSamples });
                setLoadState("ready");
            })
            .catch(error => {
                if (cancelled) return;
                setLoadError(error instanceof Error
                    ? error.message
                    : "No se pudo cargar el catálogo de campos del XSD.");
                setLoadState("error");
                toast.error(error instanceof Error
                    ? error.message
                    : "No se pudieron cargar los campos y valores iniciales del XSD.");
            });

        return () => {
            cancelled = true;
        };
    }, [isVersioned, sampleDataStorageKey, xsdId, xsdVersion]);

    useEffect(() => {
        if (!activePanel) return undefined;
        const closePanelOnEscape = event => {
            if (event.key === "Escape") setActivePanel(null);
        };
        window.addEventListener("keydown", closePanelOnEscape);
        return () => window.removeEventListener("keydown", closePanelOnEscape);
    }, [activePanel]);

    useEffect(() => {
        if (!previewOpen) return undefined;
        const updatePreviewForViewport = () => {
            setPreviewHtml(buildCertificatePreviewHtml(history.present, window.innerWidth, { dataContext: sampleData }));
        };
        window.addEventListener("resize", updatePreviewForViewport);
        return () => window.removeEventListener("resize", updatePreviewForViewport);
    }, [history.present, previewOpen, sampleData]);

    const updateDocument = (updater, recordHistory = true) => {
        setHistory(current => {
            const nextPresent = updater(current.present);
            if (nextPresent === current.present) return current;
            return {
                past: recordHistory
                    ? [...current.past.slice(-(HISTORY_LIMIT - 1)), current.present]
                    : current.past,
                present: nextPresent,
                future: recordHistory ? [] : current.future
            };
        });
    };

    const beginHistoryStep = () => {
        setHistory(current => ({
            ...current,
            past: [...current.past.slice(-(HISTORY_LIMIT - 1)), current.present],
            future: []
        }));
    };

    const undo = useCallback(() => {
        setHistory(current => {
            if (current.past.length === 0) return current;
            return {
                past: current.past.slice(0, -1),
                present: current.past[current.past.length - 1],
                future: [current.present, ...current.future]
            };
        });
    }, []);

    const redo = useCallback(() => {
        setHistory(current => {
            if (current.future.length === 0) return current;
            const [next, ...future] = current.future;
            return {
                past: [...current.past, current.present],
                present: next,
                future
            };
        });
    }, []);

    useEffect(() => {
        const handleHistoryShortcut = event => {
            if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
            const target = event.target;
            if (target instanceof HTMLElement && (target.isContentEditable || target.closest("input, textarea, select"))) return;

            if (event.key.toLowerCase() === "z") {
                event.preventDefault();
                if (event.shiftKey) redo();
                else undo();
            } else if (event.key.toLowerCase() === "y") {
                event.preventDefault();
                redo();
            }
        };

        window.addEventListener("keydown", handleHistoryShortcut);
        return () => window.removeEventListener("keydown", handleHistoryShortcut);
    }, [redo, undo]);

    const addElement = (type, position = {}) => {
        const element = {
            id: `${Date.now()}-${Math.random()}`,
            type,
            x: position.x ?? 80,
            y: position.y ?? 80,
            width: type === "line" ? 300 : type === "image" ? 180 : type === "rectangle" || type === "ellipse" ? 160 : 220,
            height: type === "line" ? 3 : type === "image" ? 120 : type === "rectangle" || type === "ellipse" ? 100 : type === "checkbox" ? 38 : 45,
            text: type === "text" ? "Nuevo texto" : type === "checkbox" ? "Opción verificada" : "",
            variable: type === "dynamic" ? "ciudadano.nombre" : "",
            checked: false,
            imageSrc: "",
            imageLoadError: false,
            fontSize: 16,
            fontWeight: "normal",
            textAlign: "left",
            color: "#1F2937",
            background: "transparent",
            fillColor: "#DCE8F5",
            borderColor: "#315B7D",
            borderWidth: 2,
            borderEnabled: true
        };

        updateDocument(document => ({
            ...document,
            elements: [...document.elements, element]
        }));
        setSelectedElement(element.id);
    };

    const updateElement = (id, changes, recordHistory = true) => {
        updateDocument(document => {
            const element = document.elements.find(item => item.id === id);
            if (!element || Object.entries(changes).every(([key, value]) => element[key] === value)) return document;
            return {
                ...document,
                elements: document.elements.map(item => item.id === id ? { ...item, ...changes } : item)
            };
        }, recordHistory);
    };

    const moveLayer = (id, direction) => {
        updateDocument(document => {
            const index = document.elements.findIndex(element => element.id === id);
            const targetIndex = index + (direction === "up" ? 1 : -1);
            if (index < 0 || targetIndex < 0 || targetIndex >= document.elements.length) return document;

            const nextElements = [...document.elements];
            [nextElements[index], nextElements[targetIndex]] = [nextElements[targetIndex], nextElements[index]];
            return { ...document, elements: nextElements };
        });
    };

    const toggleLayerVisibility = id => {
        updateDocument(document => ({
            ...document,
            elements: document.elements.map(element => element.id === id
                ? { ...element, visible: element.visible === false }
                : element)
        }));
    };

    const deleteElement = id => {
        updateDocument(document => ({
            ...document,
            elements: document.elements.filter(element => element.id !== id)
        }));
        setSelectedElement(null);
    };

    const duplicateElement = id => {
        const element = elements.find(item => item.id === id);
        if (!element) return;
        const copy = {
            ...element,
            id: `${Date.now()}-${Math.random()}`,
            x: element.x + 20,
            y: element.y + 20
        };
        updateDocument(document => ({ ...document, elements: [...document.elements, copy] }));
        setSelectedElement(copy.id);
    };

    const updatePageStyle = nextPageStyle => {
        updateDocument(document => ({ ...document, pageStyle: nextPageStyle }));
    };

    const updateSampleValue = (field, value) => {
        setSampleData(current => ({ ...current, [field]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            window.localStorage.setItem(documentStorageKey, JSON.stringify(history.present));
            window.localStorage.setItem(sampleDataStorageKey, JSON.stringify(sampleData));
        } catch {
            toast.error("No se pudieron guardar los cambios. El almacenamiento del navegador podría estar lleno.");
            return;
        } finally {
            setIsSaving(false);
        }
        setPdfError("");
        toast.success(isVersioned
            ? "Diseño y datos de ejemplo guardados en este navegador para esta versión del XSD."
            : "Cambios guardados correctamente.");
    };
    const handleDownloadJson = () => downloadCertificateJson(history.present);
    const handleGeneratePdf = async () => {
        setPdfError("");
        try {
            await downloadCertificatePdf(history.present, window, { dataContext: sampleData });
        } catch (error) {
            setPdfError(error instanceof Error
                ? `No se pudo generar el PDF: ${error.message}`
                : "No se pudo generar el PDF. Intenta nuevamente.");
        }
    };
    const handlePreview = () => {
        setPreviewHtml(buildCertificatePreviewHtml(history.present, window.innerWidth, { dataContext: sampleData }));
        setPreviewOpen(true);
    };

    const toggleGrid = () => setGridVisible(visible => !visible);
    const toggleSnapToGrid = () => setSnapToGrid(enabled => {
        if (!enabled) setGridVisible(true);
        return !enabled;
    });

    const togglePanel = panel => {
        if (panel === "properties") setPropertiesCollapsed(false);
        setActivePanel(current => current === panel ? null : panel);
    };

    const selected = elements.find(element => element.id === selectedElement);
    const sampleDataFields = Array.from(new Set(
        elements
            .filter(element => element.type === "dynamic" && element.variable)
            .map(element => element.variable)
    ));

    if (requiresVersion) {
        return (
            <div className="certificate-builder-load-state">
                <p role="alert">Selecciona una versión del XSD antes de abrir el editor.</p>
                <button type="button" onClick={onBack}>Regresar</button>
            </div>
        );
    }

    if (isVersioned && loadState === "loading") {
        return <div className="certificate-builder-load-state" role="status">Cargando campos de la versión {xsdVersion}...</div>;
    }

    if (isVersioned && loadState === "error") {
        return (
            <div className="certificate-builder-load-state">
                <p role="alert">{loadError}</p>
                <button type="button" onClick={onBack}>Regresar</button>
            </div>
        );
    }

    return (
        <div className={`certificate-editor${isMobileEditor ? " certificate-editor-mobile" : ""}`}>
            {isMobileEditor ? (
                <MobileCertificateEditor
                    onBack={onBack}
                    xsdName={xsdName}
                    xsdVersion={xsdVersion}
                    canUndo={history.past.length > 0}
                    canRedo={history.future.length > 0}
                    onUndo={undo}
                    onRedo={redo}
                    onSave={handleSave}
                    isSaving={isSaving}
                    activePanel={activePanel}
                    onSetActivePanel={setActivePanel}
                    elements={elements}
                    selectedElement={selectedElement}
                    selected={selected}
                    onAddElement={addElement}
                    onSelectElement={setSelectedElement}
                    onMoveLayer={moveLayer}
                    onToggleLayerVisibility={toggleLayerVisibility}
                    onUpdateElement={updateElement}
                    dynamicFields={dynamicFields}
                    onDeleteElement={deleteElement}
                    onDuplicateElement={duplicateElement}
                    onBeginHistoryStep={beginHistoryStep}
                    pageStyle={pageStyle}
                    onUpdatePageStyle={updatePageStyle}
                    gridVisible={gridVisible}
                    onToggleGrid={toggleGrid}
                    snapToGrid={snapToGrid}
                    onToggleSnap={toggleSnapToGrid}
                    zoom={canvasZoom}
                    onZoomChange={setCanvasZoom}
                    onFitZoom={() => setCanvasZoom(1)}
                    onToggleCollapse={() => setPropertiesCollapsed(collapsed => !collapsed)}
                    onPreview={handlePreview}
                    onOpenSampleData={() => setSampleDataOpen(true)}
                    onDownloadJson={handleDownloadJson}
                    onGeneratePdf={handleGeneratePdf}
                    pdfError={pdfError}
                />
            ) : (
                <>
            <header className="editor-header">
                <button className="editor-back" onClick={onBack}>
                    <FaArrowLeft />
                    Regresar
                </button>

                <div className="editor-title">
                    <h1>Certificado personalizado</h1>
                    <span>
                        {xsdName
                            ? `XSD: ${xsdName}${xsdVersion ? ` · Versión ${xsdVersion}` : ""}`
                            : "Constructor visual de certificados"}
                    </span>
                </div>

                <div className="editor-history-actions">
                    <button type="button" className="editor-tool" aria-label="Deshacer" title="Deshacer (Ctrl+Z)" onClick={undo} disabled={history.past.length === 0}>
                        <FaUndo />
                    </button>
                    <button type="button" className="editor-tool" aria-label="Rehacer" title="Rehacer (Ctrl+Y)" onClick={redo} disabled={history.future.length === 0}>
                        <FaRedo />
                    </button>
                    <button type="button" className="editor-tool" aria-label="Mostrar cuadrícula" title="Mostrar u ocultar cuadrícula" aria-pressed={gridVisible} onClick={toggleGrid}>
                        <FaTh />
                    </button>
                    <button type="button" className="editor-tool" aria-label="Ajustar a cuadrícula" title="Ajustar elementos a la cuadrícula" aria-pressed={snapToGrid} onClick={toggleSnapToGrid}>
                        <FaMagnet />
                    </button>
                    <button
                        type="button"
                        className="editor-tool"
                        aria-label="Zoom del lienzo"
                        title="Ajustar el zoom del lienzo"
                        aria-expanded={activePanel === "zoom"}
                        aria-pressed={activePanel === "zoom"}
                        onClick={() => togglePanel("zoom")}
                    >
                        <FaSearch aria-hidden="true" />
                    </button>
                </div>

                <div className="editor-panel-actions">
                    <button
                        type="button"
                        className="editor-tool editor-panel-trigger editor-panel-trigger-responsive"
                        aria-label="Mostrar componentes"
                        aria-expanded={activePanel === "components"}
                        aria-pressed={activePanel === "components"}
                        onClick={() => togglePanel("components")}
                    >
                        <FaFont aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="editor-tool editor-panel-trigger editor-panel-trigger-responsive"
                        aria-label="Mostrar propiedades"
                        aria-expanded={activePanel === "properties"}
                        aria-pressed={activePanel === "properties"}
                        onClick={() => togglePanel("properties")}
                    >
                        <FaEdit aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="editor-tool editor-panel-trigger"
                        aria-label="Mostrar capas"
                        aria-expanded={activePanel === "layers"}
                        aria-pressed={activePanel === "layers"}
                        onClick={() => togglePanel("layers")}
                    >
                        <FaLayerGroup aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="editor-tool editor-panel-trigger"
                        aria-label="Propiedades de hoja"
                        aria-expanded={activePanel === "page"}
                        aria-pressed={activePanel === "page"}
                        onClick={() => togglePanel("page")}
                    >
                        <FaFileAlt aria-hidden="true" />
                    </button>
                </div>

                <div className="editor-actions">
                    <button type="button" className="editor-save" onClick={handleSave} disabled={isSaving} title="Guardar los cambios localmente">
                        <FaSave />
                        {isSaving ? "Guardando..." : "Guardar cambios"}
                    </button>
                    <button type="button" className="editor-preview" onClick={() => setSampleDataOpen(true)} title="Editar datos de ejemplo">
                        <FaDatabase />
                        Datos de ejemplo
                    </button>
                    <button type="button" className="editor-preview" onClick={handlePreview} title="Previsualizar el HTML generado">
                        <FaEye />
                        Previsualizar
                    </button>
                    <button type="button" className="editor-json" onClick={handleDownloadJson} title="Descargar el documento JSON">
                        <FaFileCode />
                        Descargar JSON
                    </button>
                    <button type="button" className="editor-pdf" onClick={handleGeneratePdf}>
                        <FaFilePdf />
                        Generar PDF
                    </button>
                </div>
            </header>

            {pdfError && <div className="editor-print-error" role="alert">{pdfError}</div>}

            <div className={`editor-workspace${propertiesCollapsed ? " editor-workspace-properties-collapsed" : ""}`}>
                <EditorSidebar
                    onAddElement={(type, position) => {
                        addElement(type, position);
                        setActivePanel(current => current === "components" ? null : current);
                    }}
                    elements={elements}
                    selectedElement={selectedElement}
                    onSelectElement={setSelectedElement}
                    onMoveLayer={moveLayer}
                    onToggleLayerVisibility={toggleLayerVisibility}
                    componentsOpen={activePanel === "components"}
                    onCloseComponents={() => setActivePanel(null)}
                    layersOpen={activePanel === "layers"}
                    onCloseLayers={() => setActivePanel(null)}
                />
                <EditorCanvas
                    elements={elements}
                    selectedElement={selectedElement}
                    onSelectElement={setSelectedElement}
                    onAddElement={addElement}
                    onUpdateElement={updateElement}
                    onDeleteElement={deleteElement}
                    onDuplicateElement={duplicateElement}
                    onBeginHistoryStep={beginHistoryStep}
                    pageStyle={pageStyle}
                    gridVisible={gridVisible}
                    snapToGrid={snapToGrid}
                    zoom={canvasZoom}
                />
                <EditorPropiedades
                    element={selected}
                    onUpdate={updateElement}
                    dynamicFields={dynamicFields}
                    collapsed={propertiesCollapsed}
                    onToggleCollapse={() => setPropertiesCollapsed(collapsed => !collapsed)}
                    panelOpen={activePanel === "properties"}
                    onClosePanel={() => setActivePanel(null)}
                />
                {activePanel && (
                    <button
                        type="button"
                        className="editor-panel-backdrop"
                        aria-label="Cerrar panel activo"
                        onClick={() => setActivePanel(null)}
                    />
                )}
                {activePanel === "page" && (
                    <EditorPageProperties
                        pageStyle={pageStyle}
                        onChange={updatePageStyle}
                        onClose={() => setActivePanel(null)}
                    />
                )}
                {activePanel === "zoom" && (
                    <section className="editor-zoom-panel" role="dialog" aria-modal="false" aria-labelledby="editor-zoom-title">
                        <header className="editor-floating-panel-header">
                            <h2 id="editor-zoom-title">Zoom del lienzo</h2>
                            <button type="button" aria-label="Cerrar zoom" onClick={() => setActivePanel(null)}>
                                <FaTimes aria-hidden="true" />
                            </button>
                        </header>
                        <CanvasZoomControls
                            zoom={canvasZoom}
                            onZoomChange={setCanvasZoom}
                            onFit={() => setCanvasZoom(1)}
                        />
                    </section>
                )}
            </div>
                </>
            )}

            {previewOpen && (
                <div className="certificate-preview-backdrop" onMouseDown={event => {
                    if (event.target === event.currentTarget) {
                        setPreviewOpen(false);
                        setPreviewHtml(null);
                    }
                }}>
                    <section className="certificate-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="certificate-preview-title">
                        <header className="certificate-preview-header">
                            <div>
                                <h2 id="certificate-preview-title">Vista previa del HTML</h2>
                                <span>Representación descargable del certificado</span>
                            </div>
                            <button type="button" className="editor-tool" aria-label="Cerrar vista previa" onClick={() => {
                                setPreviewOpen(false);
                                setPreviewHtml(null);
                            }}>
                                <FaTimes />
                            </button>
                        </header>
                        <iframe
                            title="Vista previa del certificado HTML"
                            sandbox=""
                            srcDoc={previewHtml}
                        />
                    </section>
                </div>
            )}
            {sampleDataOpen && (
                <CertificateSampleDataPanel
                    fields={sampleDataFields}
                    values={sampleData}
                    onChange={updateSampleValue}
                    onClose={() => setSampleDataOpen(false)}
                    xsdVersion={xsdVersion}
                />
            )}
        </div>
    );
}

export default CertificadosEditor;
